import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "block text-sm font-medium text-gray-700 mb-1" }
const _hoisted_2 = {
  key: 0,
  class: "text-red-500"
}
const _hoisted_3 = ["value", "placeholder", "required", "disabled"]
const _hoisted_4 = {
  key: 0,
  class: "mt-1 text-sm text-red-600"
}
const _hoisted_5 = {
  key: 1,
  class: "mt-1 text-sm text-gray-500"
}
const _hoisted_6 = {
  key: 2,
  class: "mt-1 text-sm text-green-600"
}

import { computed, ref, watch } from 'vue';
import type { EmailFieldProps } from '@/types/sellerRegister';
import { checkEmailExists } from '@/api/sellerRegister';


export default /*@__PURE__*/_defineComponent({
  __name: 'EmailField',
  props: {
    label: {},
    required: { type: Boolean },
    placeholder: {},
    modelValue: {},
    error: { type: Boolean },
    disabled: { type: Boolean },
    gridClass: {}
  },
  emits: ["update:modelValue", "validation-changed"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Constante pour la validation email (même que dans le login)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// État local pour la validation
const errorMessage = ref('');
const hasBeenTouched = ref(false);
const isCheckingEmail = ref(false);
const emailExists = ref(false);
let checkEmailTimeout: ReturnType<typeof setTimeout> | null = null;

// Classes CSS pour l'input
const inputClasses = computed(() => {
  const baseClasses = 'focus:outline-none';
  
  if (errorMessage.value) {
    return `${baseClasses} border-red-500`;
  } else if (props.modelValue && isValidEmail.value) {
    return `${baseClasses} border-green-500`;
  } else {
    return `${baseClasses} border-gray-300`;
  }
});

// Validation de l'email
const isValidEmail = computed(() => {
  if (!props.modelValue) return false;
  return EMAIL_REGEX.test(props.modelValue);
});

// Vérifier si l'email existe déjà sur le serveur
const checkEmailOnServer = async (email: string) => {
  if (!email || !isValidEmail.value) {
    emailExists.value = false;
    return;
  }

  isCheckingEmail.value = true;
  try {
    const exists = await checkEmailExists(email);
    emailExists.value = exists;
    
    if (exists) {
      errorMessage.value = 'Cet email est déjà utilisé';
      emit('validation-changed', false);
    } else {
      // Si l'email n'existe pas et que le format est valide, on valide
      if (isValidEmail.value) {
        errorMessage.value = '';
        emit('validation-changed', true);
      }
    }
  } catch (error) {
    console.error('Erreur lors de la vérification de l\'email:', error);
    // En cas d'erreur, on ne bloque pas l'utilisateur
    emailExists.value = false;
  } finally {
    isCheckingEmail.value = false;
  }
};

// Validation complète avec messages d'erreur
const validateEmail = async () => {
  if (!props.modelValue) {
    errorMessage.value = '';
    emailExists.value = false;
    emit('validation-changed', false);
    return false;
  }
  
  if (!isValidEmail.value) {
    errorMessage.value = 'Veuillez saisir un email valide';
    emailExists.value = false;
    emit('validation-changed', false);
    return false;
  }
  
  // Si l'email existe déjà, on ne vérifie pas à nouveau
  if (emailExists.value) {
    errorMessage.value = 'Cet email est déjà utilisé';
    emit('validation-changed', false);
    return false;
  }
  
  // Vérifier sur le serveur avec debounce
  if (checkEmailTimeout) {
    clearTimeout(checkEmailTimeout);
  }
  
  checkEmailTimeout = setTimeout(() => {
    checkEmailOnServer(props.modelValue);
  }, 500); // Debounce de 500ms
  
  return true;
};

// Gérer les changements d'input
const onInput = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const value = target.value;
  
  emit('update:modelValue', value);
  
  // Réinitialiser l'état d'existence si l'email change
  if (emailExists.value) {
    emailExists.value = false;
    errorMessage.value = '';
  }
  
  // Valider et émettre l'état
  await validateEmail();
};

// Gérer la perte de focus
const onBlur = async () => {
  hasBeenTouched.value = true;
  
  // Valider seulement si le champ a été touché ou s'il y a une valeur
  if (hasBeenTouched.value || props.modelValue) {
    // Annuler le debounce en cours et vérifier immédiatement
    if (checkEmailTimeout) {
      clearTimeout(checkEmailTimeout);
      checkEmailTimeout = null;
    }
    await validateEmail();
  }
};

// Watcher pour la validation externe
watch(() => props.modelValue, (newValue) => {
  if (hasBeenTouched.value || newValue) {
    validateEmail();
  }
}, { immediate: true });

// Watcher pour la prop error externe
watch(() => props.error, (newError) => {
  if (newError && !errorMessage.value) {
    errorMessage.value = 'Email invalide';
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(_ctx.gridClass)
  }, [
    _createElementVNode("label", _hoisted_1, [
      _createTextVNode(_toDisplayString(_ctx.label) + " ", 1),
      (_ctx.required)
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("input", {
      value: _ctx.modelValue,
      onInput: onInput,
      onBlur: onBlur,
      type: "email",
      placeholder: _ctx.placeholder,
      required: _ctx.required,
      disabled: _ctx.disabled,
      class: _normalizeClass(["w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm", inputClasses.value]),
      autocomplete: "email"
    }, null, 42, _hoisted_3),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(errorMessage.value), 1))
      : _createCommentVNode("", true),
    (isCheckingEmail.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, " Vérification en cours... "))
      : _createCommentVNode("", true),
    (_ctx.modelValue && !errorMessage.value && isValidEmail.value && !isCheckingEmail.value && !emailExists.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, " ✓ Email valide et disponible "))
      : _createCommentVNode("", true)
  ], 2))
}
}

})